import React, { useState } from 'react';
import { Share2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { sharePromotionOnKakao } from '../../utils/kakaoShare';

export default function PromotionLinkShare({ title, url }: { title: string; url: string }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<'copied' | 'failed' | 'kakao-failed' | null>(null);
  const kakaoKey = process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY?.trim();

  const handleCopy = async () => {
    setMessage(await copyToClipboard(url) ? 'copied' : 'failed');
  };

  const handleShare = async () => {
    setOpen(false);
    setMessage(null);
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError') return;
      }
    }

    await handleCopy();
  };

  const handleKakaoShare = async () => {
    if (!kakaoKey) return;
    setOpen(false);
    setMessage(null);
    try {
      await sharePromotionOnKakao(title, url, kakaoKey);
    } catch {
      setMessage('kakao-failed');
    }
  };

  return (
    <aside className="promotion-share" aria-label="프로모션 링크 공유">
      <p>* PC로 보려면 사용하세요</p>
      <Popover open={open} onOpenChange={nextOpen => { setOpen(nextOpen); if (nextOpen) setMessage(null); }}>
        <PopoverTrigger asChild>
          <button type="button" className="promotion-share-button">
            <Share2 aria-hidden="true" size={16} />
            링크 공유
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" collisionPadding={12} className="promotion-theme promotion-share-menu">
          <div role="group" aria-label="공유 방법">
            <button type="button" disabled={!kakaoKey} onClick={handleKakaoShare}>
              카카오톡으로 공유{!kakaoKey && ' (설정 필요)'}
            </button>
            <button type="button" onClick={handleShare}>다른 앱으로 공유</button>
            <button type="button" onClick={() => { setOpen(false); void handleCopy(); }}>링크 복사</button>
          </div>
        </PopoverContent>
      </Popover>
      {message === 'copied' && <span className="promotion-share-message" role="status">링크가 복사되었습니다.</span>}
      {message === 'kakao-failed' && <span className="promotion-share-message" role="alert">카카오톡 공유를 시작하지 못했습니다. 다른 공유 방법을 이용해 주세요.</span>}
      {message === 'failed' && <label className="promotion-share-fallback">
        링크 복사에 실패했습니다. 아래 링크를 직접 복사해 주세요.
        <input type="url" readOnly value={url} onFocus={event => event.currentTarget.select()} aria-label="복사할 프로모션 링크" />
      </label>}
    </aside>
  );
}
